import React, { useState, useEffect } from 'react';
import { X, CreditCard, Wallet as WalletIcon } from 'lucide-react';
import { showToast } from './ToastContainer';
import { createWithdrawal } from '../utils/balance';
import { getCoinGateStatus } from '../utils/coingate';
import { useCurrency } from '../contexts/CurrencyContext';
import '../styles/WithdrawalModal.css';

const WithdrawalModal = ({ isOpen, onClose, balance, currency, onSuccess }) => {
  const { currentCurrency, formatCurrency: formatCurrencyAmount, getCurrencySymbol } = useCurrency();
  const [selectedMethod, setSelectedMethod] = useState('stripe');
  const [amount, setAmount] = useState('');
  const [paypalEmail, setPaypalEmail] = useState('');
  const [cryptoWalletAddress, setCryptoWalletAddress] = useState('');
  const [cryptoCurrency, setCryptoCurrency] = useState('BTC');
  const [coingateEmail, setCoingateEmail] = useState('');
  const [coingateFirstName, setCoingateFirstName] = useState('');
  const [coingateSurname, setCoingateSurname] = useState('');
  const [coingateDob, setCoingateDob] = useState('');
  const [coingateCountry, setCoingateCountry] = useState('USA'); // alpha-3 (e.g. USA, LTU)
  const [processing, setProcessing] = useState(false);
  const [coingateConnected, setCoingateConnected] = useState(false);
  const [coingateStatus, setCoingateStatus] = useState(null);
  const [selectedPayoutSetting, setSelectedPayoutSetting] = useState(null);
  
  // Use user's selected currency for display and input
  const displayCurrency = currentCurrency;

  // Check CoinGate connection status when modal opens
  useEffect(() => {
    if (isOpen && selectedMethod === 'coingate') {
      getCoinGateStatus()
        .then((status) => {
          setCoingateStatus(status);
          setCoingateConnected(status.connected);
          if (status.connected && status.payout_settings && status.payout_settings.length > 0) {
            // Pre-fill with primary payout setting or first one
            const primary = status.payout_settings.find(s => s.is_primary) || status.payout_settings[0];
            setSelectedPayoutSetting(primary.id);
            setCryptoWalletAddress(primary.wallet_address || '');
            setCryptoCurrency(primary.crypto_currency || 'BTC');
          }
        })
        .catch((error) => {
          console.error('Failed to load CoinGate status:', error);
          setCoingateConnected(false);
        });
    }
  }, [isOpen, selectedMethod]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!amount || parseFloat(amount) <= 0) {
      showToast('Please enter a valid amount', 'error');
      return;
    }

    const withdrawalAmount = parseFloat(amount);
    // Minimum withdrawal amount in user's selected currency (USD)
    const minAmountDisplay = 10;

    if (withdrawalAmount < minAmountDisplay) {
      showToast(`Minimum withdrawal amount is ${getCurrencySymbol()}${minAmountDisplay}`, 'error');
      return;
    }

    if (withdrawalAmount > parseFloat(balance || 0)) {
      showToast('Insufficient balance', 'error');
      return;
    }

    if (selectedMethod === 'paypal' && !paypalEmail) {
      showToast('Please enter your PayPal email address', 'error');
      return;
    }

    // Basic email validation for PayPal
    if (selectedMethod === 'paypal') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(paypalEmail)) {
        showToast('Please enter a valid PayPal email address', 'error');
        return;
      }
    }

    // Validate CoinGate - if connected, just need amount. If not, need all beneficiary details
    if (selectedMethod === 'coingate') {
      if (coingateConnected) {
        // User has connected CoinGate - no need for beneficiary details
        // Wallet address and currency are already set from connection
        if (!cryptoWalletAddress || cryptoWalletAddress.trim().length < 10) {
          showToast('CoinGate connection error: wallet address missing', 'error');
          return;
        }
      } else {
        // User hasn't connected CoinGate - need all beneficiary details
        if (!cryptoWalletAddress || cryptoWalletAddress.trim().length < 10) {
          showToast('Please enter a valid crypto wallet address', 'error');
          return;
        }

        if (!coingateEmail || !coingateFirstName || !coingateSurname || !coingateDob || !coingateCountry) {
          showToast('Please fill all CoinGate beneficiary details (email, name, DOB, country)', 'error');
          return;
        }

        // Basic email validation for CoinGate
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(coingateEmail)) {
          showToast('Please enter a valid email address for CoinGate', 'error');
          return;
        }

        // CoinGate doesn't accept temporary/example emails
        const tempEmailDomains = ['example.com', 'test.com', 'temp.com', 'mailinator.com', '10minutemail.com'];
        const emailDomain = coingateEmail.split('@')[1]?.toLowerCase();
        if (tempEmailDomains.includes(emailDomain)) {
          showToast('CoinGate requires a real email address (not temporary emails like @example.com)', 'error');
          return;
        }
      }
    }

    setProcessing(true);
    try {
      // Send amount in user's selected currency (USD) - no conversion needed
      const withdrawalData = {
        amount: withdrawalAmount,
        payment_method: selectedMethod,
        currency: displayCurrency,
      };

      // Add PayPal email if PayPal is selected
      if (selectedMethod === 'paypal') {
        withdrawalData.paypal_email = paypalEmail;
      }

      // Add CoinGate wallet address and currency if CoinGate is selected
      if (selectedMethod === 'coingate') {
        withdrawalData.crypto_wallet_address = cryptoWalletAddress.trim();
        withdrawalData.crypto_currency = cryptoCurrency;
        // Only send beneficiary details if CoinGate is NOT connected (legacy flow)
        if (!coingateConnected) {
          withdrawalData.coingate_beneficiary = {
            beneficiary_type: 'person',
            email: coingateEmail.trim(),
            first_name: coingateFirstName,
            surname: coingateSurname,
            date_of_birth: coingateDob, // YYYY-MM-DD
            country: coingateCountry, // alpha-3 (e.g. USA)
          };
        }
      }

      await createWithdrawal(
        withdrawalAmount,
        selectedMethod,
        displayCurrency,
        paypalEmail,
        cryptoWalletAddress,
        cryptoCurrency,
        withdrawalData.coingate_beneficiary
      );
      showToast('Withdrawal request submitted successfully', 'success');
      setAmount('');
      setPaypalEmail('');
      setCryptoWalletAddress('');
      setCryptoCurrency('BTC');
      setCoingateEmail('');
      setCoingateFirstName('');
      setCoingateSurname('');
      setCoingateDob('');
      setCoingateCountry('USA');
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      showToast(error.message || 'Failed to process withdrawal', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const paymentMethods = [
    {
      id: 'stripe',
      name: 'Stripe',
      icon: <CreditCard size={24} />,
      description: 'Withdraw to your bank account via Stripe',
      available: true,
    },
    {
      id: 'paypal',
      name: 'PayPal',
      icon: <WalletIcon size={24} />,
      description: 'Withdraw to your PayPal account',
      available: true,
    },
    {
      id: 'coingate',
      name: 'CoinGate',
      icon: <WalletIcon size={24} />,
      description: 'Withdraw via cryptocurrency',
      available: true,
      comingSoon: false,
    },
  ];

  return (
    <div className="withdrawal-modal-overlay" onClick={onClose}>
      <div className="withdrawal-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="withdrawal-modal-header">
          <h2>Withdraw Funds</h2>
          <button className="withdrawal-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="withdrawal-modal-form">
          <div className="withdrawal-balance-info">
            <span>Available Balance:</span>
            <span className="withdrawal-balance-amount">
              {formatCurrencyAmount(parseFloat(balance || 0), displayCurrency)}
            </span>
          </div>

          <div className="withdrawal-method-selection">
            <label>Select Payment Method</label>
            <div className="withdrawal-methods-grid">
              {paymentMethods.map((method) => (
                <button
                  key={method.id}
                  type="button"
                  className={`withdrawal-method-card ${selectedMethod === method.id ? 'selected' : ''} ${!method.available ? 'disabled' : ''}`}
                  onClick={() => method.available && setSelectedMethod(method.id)}
                  disabled={!method.available}
                >
                  <div className="withdrawal-method-icon">{method.icon}</div>
                  <div className="withdrawal-method-info">
                    <div className="withdrawal-method-name">
                      {method.name}
                      {method.comingSoon && (
                        <span className="coming-soon-badge">Coming Soon</span>
                      )}
                    </div>
                    <div className="withdrawal-method-description">{method.description}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="withdrawal-amount-input">
            <label htmlFor="withdrawal-amount">
              Amount (Minimum: {getCurrencySymbol()}10 {displayCurrency})
            </label>
            <input
              id="withdrawal-amount"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={`Enter amount (min ${getCurrencySymbol()}10)`}
              min="10"
              step="0.01"
              max={balance || 0}
              required
              disabled={processing}
            />
            <p style={{ 
              marginTop: '0.5rem', 
              fontSize: '0.75rem', 
              color: '#a0a0a0',
              fontFamily: 'Gilroy, sans-serif'
            }}>
              Withdrawal will be processed in {displayCurrency}
            </p>
          </div>

          {selectedMethod === 'paypal' && (
            <div className="withdrawal-amount-input">
              <label htmlFor="paypal-email">
                PayPal Email Address
              </label>
              <input
                id="paypal-email"
                type="email"
                value={paypalEmail}
                onChange={(e) => setPaypalEmail(e.target.value)}
                placeholder="your.email@example.com"
                required
                disabled={processing}
              />
              <p style={{ 
                marginTop: '0.5rem', 
                fontSize: '0.75rem', 
                color: '#a0a0a0',
                fontFamily: 'Gilroy, sans-serif'
              }}>
                Enter the email address associated with your PayPal account
              </p>
            </div>
          )}

          {selectedMethod === 'coingate' && (
            <>
              {coingateConnected ? (
                <div style={{ 
                  padding: '1rem', 
                  background: 'rgba(16, 185, 129, 0.1)', 
                  border: '1px solid rgba(16, 185, 129, 0.3)', 
                  borderRadius: '4px',
                  marginBottom: '1rem',
                  fontFamily: 'Gilroy, sans-serif',
                }}>
                  <div style={{ color: '#10b981', marginBottom: '0.75rem', fontWeight: 600 }}>
                    ✓ CoinGate Connected
                  </div>
                  
                  {coingateStatus?.payout_settings && coingateStatus.payout_settings.length > 1 ? (
                    <div className="withdrawal-amount-input" style={{ marginBottom: '0' }}>
                      <label htmlFor="payout-setting-select" style={{ marginBottom: '0.5rem' }}>
                        Select Payout Setting
                      </label>
                      <select
                        id="payout-setting-select"
                        value={selectedPayoutSetting || ''}
                        onChange={(e) => {
                          const setting = coingateStatus.payout_settings.find(s => s.id === e.target.value);
                          if (setting) {
                            setSelectedPayoutSetting(setting.id);
                            setCryptoWalletAddress(setting.wallet_address);
                            setCryptoCurrency(setting.crypto_currency);
                          }
                        }}
                        required
                        disabled={processing}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          borderRadius: '4px',
                          border: '1px solid #252525',
                          background: 'rgba(255, 255, 255, 0.1)',
                          color: '#fff',
                          fontFamily: 'Gilroy, sans-serif',
                          fontSize: '1rem',
                        }}
                      >
                        {coingateStatus.payout_settings.map((setting) => (
                          <option key={setting.id} value={setting.id}>
                            {setting.crypto_currency} - {setting.wallet_address?.substring(0, 10)}...{setting.wallet_address?.substring(setting.wallet_address.length - 8)} {setting.is_primary ? '(Primary)' : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : coingateStatus?.payout_settings && coingateStatus.payout_settings.length === 1 ? (
                    <div>
                      <div style={{ color: '#a0a0a0', fontSize: '0.875rem' }}>
                        Currency: {coingateStatus.payout_settings[0].crypto_currency}
                      </div>
                      <div style={{ color: '#a0a0a0', fontSize: '0.875rem' }}>
                        Wallet: {coingateStatus.payout_settings[0].wallet_address?.substring(0, 10)}...{coingateStatus.payout_settings[0].wallet_address?.substring(coingateStatus.payout_settings[0].wallet_address.length - 8)}
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : (
                <div style={{ 
                  padding: '1rem', 
                  background: 'rgba(239, 68, 68, 0.1)', 
                  border: '1px solid rgba(239, 68, 68, 0.3)', 
                  borderRadius: '4px',
                  marginBottom: '1rem',
                  fontFamily: 'Gilroy, sans-serif',
                }}>
                  <div style={{ color: '#ef4444', marginBottom: '0.5rem', fontWeight: 600 }}>
                    CoinGate Not Connected
                  </div>
                  <div style={{ color: '#a0a0a0', fontSize: '0.875rem' }}>
                    Connect CoinGate in Settings → Payments to enable instant withdrawals without entering details each time.
                  </div>
                </div>
              )}
              <div className="withdrawal-amount-input">
                <label htmlFor="crypto-currency">
                  Cryptocurrency
                </label>
                <select
                  id="crypto-currency"
                  value={cryptoCurrency}
                  onChange={(e) => setCryptoCurrency(e.target.value)}
                  required
                  disabled={processing || coingateConnected}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    border: '1px solid #333',
                    background: '#1a1a1a',
                    color: '#fff',
                    fontFamily: 'Gilroy, sans-serif',
                    fontSize: '0.875rem',
                  }}
                >
                  <option value="BTC">Bitcoin (BTC)</option>
                  <option value="ETH">Ethereum (ETH)</option>
                  <option value="LTC">Litecoin (LTC)</option>
                  <option value="USDC">USD Coin (USDC)</option>
                  <option value="BNB">Binance Coin (BNB)</option>
                </select>
              </div>
              {!coingateConnected && (
                <>
                  <div className="withdrawal-amount-input">
                    <label htmlFor="crypto-wallet-address">
                      Crypto Wallet Address
                    </label>
                    <input
                      id="crypto-wallet-address"
                      type="text"
                      value={cryptoWalletAddress}
                      onChange={(e) => setCryptoWalletAddress(e.target.value)}
                      placeholder="Enter your crypto wallet address"
                      required
                      disabled={processing}
                    />
                    <p style={{ 
                      marginTop: '0.5rem', 
                      fontSize: '0.75rem', 
                      color: '#a0a0a0',
                      fontFamily: 'Gilroy, sans-serif'
                    }}>
                      Enter the wallet address where you want to receive your {cryptoCurrency} withdrawal
                    </p>
                  </div>
                  <div className="withdrawal-amount-input">
                    <label htmlFor="coingate-email">
                      Email Address (CoinGate)
                    </label>
                    <input
                      id="coingate-email"
                      type="email"
                      value={coingateEmail}
                      onChange={(e) => setCoingateEmail(e.target.value)}
                      placeholder="your.email@example.com"
                      required
                      disabled={processing}
                    />
                    <p style={{ 
                      marginTop: '0.5rem', 
                      fontSize: '0.75rem', 
                      color: '#a0a0a0',
                      fontFamily: 'Gilroy, sans-serif'
                    }}>
                      CoinGate requires a real email address (not temporary emails like @example.com)
                    </p>
                  </div>
                  <div className="withdrawal-amount-input">
                    <label htmlFor="coingate-first-name">
                      First name (CoinGate)
                    </label>
                    <input
                      id="coingate-first-name"
                      type="text"
                      value={coingateFirstName}
                      onChange={(e) => setCoingateFirstName(e.target.value)}
                      placeholder="First name"
                      required
                      disabled={processing}
                    />
                  </div>
                  <div className="withdrawal-amount-input">
                    <label htmlFor="coingate-surname">
                      Surname (CoinGate)
                    </label>
                    <input
                      id="coingate-surname"
                      type="text"
                      value={coingateSurname}
                      onChange={(e) => setCoingateSurname(e.target.value)}
                      placeholder="Surname"
                      required
                      disabled={processing}
                    />
                  </div>
                  <div className="withdrawal-amount-input">
                    <label htmlFor="coingate-dob">
                      Date of birth (CoinGate)
                    </label>
                    <input
                      id="coingate-dob"
                      type="date"
                      value={coingateDob}
                      onChange={(e) => setCoingateDob(e.target.value)}
                      required
                      disabled={processing}
                    />
                  </div>
                  <div className="withdrawal-amount-input">
                    <label htmlFor="coingate-country">
                      Country (alpha-3) (CoinGate)
                    </label>
                    <input
                      id="coingate-country"
                      type="text"
                      value={coingateCountry}
                      onChange={(e) => setCoingateCountry(e.target.value.toUpperCase())}
                      placeholder="USA"
                      required
                      disabled={processing}
                    />
                    <p style={{ 
                      marginTop: '0.5rem', 
                      fontSize: '0.75rem', 
                      color: '#a0a0a0',
                      fontFamily: 'Gilroy, sans-serif'
                    }}>
                      Use ISO alpha-3 code (e.g. USA, LTU, PAK)
                    </p>
                  </div>
                </>
              )}
            </>
          )}

          <div className="withdrawal-modal-actions">
            <button
              type="button"
              className="withdrawal-btn-cancel"
              onClick={onClose}
              disabled={processing}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="withdrawal-btn-submit"
              disabled={
                processing ||
                !amount ||
                parseFloat(amount) < 10 ||
                (selectedMethod === 'paypal' && !paypalEmail) ||
                (selectedMethod === 'coingate' &&
                  !coingateConnected &&
                  (!cryptoWalletAddress ||
                    !coingateEmail ||
                    !coingateFirstName ||
                    !coingateSurname ||
                    !coingateDob ||
                    !coingateCountry))
              }
            >
              {processing ? 'Processing...' : 'Withdraw'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WithdrawalModal;

